import React, { useEffect, useRef, useState } from 'react';
import { loadGoogleMapsScript } from '../../../utils/googleMapsLoader';
import styles from './GoogleMap.module.css';

interface GoogleMapProps {
  latitude: number;
  longitude: number;
  address?: string;
  interactive?: boolean;
  onLocationSelect?: (lat: number, lng: number) => void;
  showZoomControls?: boolean;
}

const GoogleMap: React.FC<GoogleMapProps> = ({ latitude, longitude, address, interactive = false, onLocationSelect, showZoomControls = false }) => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<any>(null);
  const markerInstance = useRef<any>(null);
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const isInitialized = useRef(false);

  // Validate coordinates are valid numbers
  const isValidCoordinates = !isNaN(latitude) && !isNaN(longitude);

  // Load Google Maps script using centralized loader
  useEffect(() => {
    loadGoogleMapsScript([])
      .then(() => {
        setScriptLoaded(true);
      })
      .catch((error: Error) => {
        setLoadError(error.message || 'Failed to load Google Maps');
      });
  }, []);

  // Initialize map when script is loaded and coordinates are valid (only once)
  useEffect(() => {
    if (!scriptLoaded) {
      return;
    }
    
    if (!isValidCoordinates) {
      return;
    }

    // Only initialize once
    if (isInitialized.current) {
      return;
    }

    const initMap = () => {
      if (!mapRef.current) {
        return;
      }

      if (!window.google || !window.google.maps) {
        return;
      }

      try {
        // Use current coordinates at initialization time
        const currentLat = latitude;
        const currentLng = longitude;
        const position = { lat: currentLat, lng: currentLng };
        
        // Dark mode style configuration
        const darkModeStyles = [
          { elementType: 'geometry', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.stroke', stylers: [{ color: '#242f3e' }] },
          { elementType: 'labels.text.fill', stylers: [{ color: '#746855' }] },
          {
            featureType: 'administrative.locality',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'geometry',
            stylers: [{ color: '#263c3f' }]
          },
          {
            featureType: 'poi.park',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#6b9a76' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry',
            stylers: [{ color: '#38414e' }]
          },
          {
            featureType: 'road',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#212a37' }]
          },
          {
            featureType: 'road',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#9ca5b3' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry',
            stylers: [{ color: '#746855' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'geometry.stroke',
            stylers: [{ color: '#1f2835' }]
          },
          {
            featureType: 'road.highway',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#f3d19c' }]
          },
          {
            featureType: 'transit',
            elementType: 'geometry',
            stylers: [{ color: '#2f3948' }]
          },
          {
            featureType: 'transit.station',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#d59563' }]
          },
          {
            featureType: 'water',
            elementType: 'geometry',
            stylers: [{ color: '#17263c' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.fill',
            stylers: [{ color: '#515c6d' }]
          },
          {
            featureType: 'water',
            elementType: 'labels.text.stroke',
            stylers: [{ color: '#17263c' }]
          }
        ];
        
        mapInstance.current = new window.google.maps.Map(mapRef.current, {
          center: position,
          zoom: 15,
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          zoomControl: showZoomControls,
          panControl: false,
          rotateControl: false,
          scaleControl: false,
          disableDefaultUI: false,
          keyboardShortcuts: true,
          clickableIcons: true,
          gestureHandling: interactive ? 'auto' : 'none',
          styles: darkModeStyles,
        });

        // Create marker
        markerInstance.current = new window.google.maps.Marker({
          map: mapInstance.current,
          position: position,
          visible: true,
        });

        // Add click listener
        if (interactive && onLocationSelect) {
          mapInstance.current.addListener('click', (e: any) => {
            const lat = e.latLng.lat();
            const lng = e.latLng.lng();
            onLocationSelect(lat, lng);
            
            // Update marker position
            markerInstance.current.setPosition({ lat, lng });
            markerInstance.current.setVisible(true);
            mapInstance.current.setCenter({ lat, lng });
          });
        } else {
          // Add click listener to open Google Maps (non-interactive mode)
          mapInstance.current.addListener('click', () => {
            const currentLat = latitude;
            const currentLng = longitude;
            const googleMapsUrl = `https://www.google.com/maps?q=${currentLat},${currentLng}`;
            window.open(googleMapsUrl, '_blank');
          });
        }

        isInitialized.current = true;
      } catch (error) {
        setLoadError('Failed to initialize map');
      }
    };

    // Small delay to ensure API is fully ready
    const timer = setTimeout(() => {
      initMap();
    }, 100);

    return () => {
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scriptLoaded, isValidCoordinates, interactive, onLocationSelect]);

  // Update map position when coordinates change (after map is initialized)
  useEffect(() => {
    if (!isInitialized.current || !mapInstance.current || !markerInstance.current) {
      return;
    }

    if (!isValidCoordinates) {
      return;
    }

    if (!window.google || !window.google.maps) {
      return;
    }

    const position = { lat: latitude, lng: longitude };
    
    // Update marker position
    markerInstance.current.setPosition(position);
    markerInstance.current.setVisible(true);
    
    // Update map center
    mapInstance.current.setCenter(position);
  }, [latitude, longitude, isValidCoordinates]);

  // Show error if coordinates are invalid or if there's a load error
  if (!isValidCoordinates || loadError) {
    return (
      <div className={styles.mapContainer}>
        <div className={styles.mapError}>
          {!isValidCoordinates ? 'Invalid coordinates provided' : loadError}
        </div>
      </div>
    );
  }

  return (
    <div className={styles.mapContainer}>
      <div ref={mapRef} className={styles.map} />
    </div>
  );
};

export default GoogleMap;

